import type { Rule } from "../types";
import { makeFiring } from "../util";

const EVIDENCE = "gabbett_2016_acwr";

/**
 * Acute:Chronic Workload Ratio (last 7d TSS / last 28d avg TSS) > 1.5.
 * Gabbett 2016: ratios above 1.5 raise injury risk substantially. Uses TSS
 * when present; falls back to duration_min as a rough surrogate when sessions
 * lack TSS values.
 */
export const acuteChronicLoadSpike: Rule = {
  id: "acute_chronic_load_spike",
  domain: "health.training",
  topics: ["training_load", "acwr", "injury_prevention"],
  evidence_refs: [EVIDENCE],
  title: () => "Training load spike",
  evaluate(ctx) {
    const now = ctx.now.getTime();
    const day = 86_400_000;
    const sevenDay = ctx.sessions.filter(
      (s) => now - new Date(s.end_ts).getTime() <= 7 * day,
    );
    const twentyEightDay = ctx.sessions.filter(
      (s) => now - new Date(s.end_ts).getTime() <= 28 * day,
    );
    if (sevenDay.length === 0 || twentyEightDay.length < 4) return null;

    const load = (s: { tss: number | null; duration_min: number }) =>
      s.tss != null && s.tss > 0 ? s.tss : s.duration_min;
    const acute = sevenDay.reduce((sum, s) => sum + load(s), 0);
    const chronic28 = twentyEightDay.reduce((sum, s) => sum + load(s), 0);
    if (chronic28 === 0) return null;
    const chronicAvg7 = chronic28 / 4;
    const ratio = acute / chronicAvg7;
    if (ratio <= 1.5) return null;

    return makeFiring({
      rule_id: "acute_chronic_load_spike",
      now: ctx.now,
      tz: ctx.tz,
      severity: ratio > 1.8 ? "bad" : "warn",
      message: `Acute:chronic load ratio ${ratio.toFixed(2)} (7d / 28d avg). Above 1.5 raises injury risk per Gabbett 2016.`,
      evidence_refs: [EVIDENCE],
      factors: [
        {
          name: "acwr",
          value: Number(ratio.toFixed(2)),
          contribution: "deload week",
          evidence_ref: EVIDENCE,
        },
      ],
      target_surface: "health.training",
      target_action: { kind: "reduce_volume", params: { pct: 30 } },
    });
  },
};
